<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { parsePhoneNumberFromString } from "libphonenumber-js";

  import phonePrefixes from "../phone-prefixes.json";
  import Stack from "./Stack.svelte";
  import Text from "./Text.svelte";
  const dispatch = createEventDispatcher<{ update: { phone: string }; submit: undefined }>();

  const prefixMap: Record<string, { name: string; code: string; callingCode: string }> = {};
  phonePrefixes.forEach((prefix) => {
    prefixMap[prefix.code] = prefix;
  });

  const countryOptions: Record<string, string> = {
    "Europe/Amsterdam": "NL",
  };
  const getDefaultCode = () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const country = countryOptions[timezone];
    return prefixMap[country || "GB"];
  };

  const defaultCode = getDefaultCode();

  export let value: string = "";
  export let prefix = defaultCode.callingCode;
  export let countryCode = defaultCode.code;
  export let error: string | null = null;
  export let disabled = false;

  const clear = () => {
    value = "";
    error = null;
  };

  let ref: any;

  function openSelect() {}

  function prefixChanged(e: any) {
    const selected = prefixMap[e.target.value];
    prefix = selected.callingCode;
    countryCode = selected.code;
    const parsed = parsePhoneNumberFromString(prefix + value);

    if (parsed) {
      dispatch("update", {
        phone: parsed.number,
      });
    }
  }

  function phoneChanged(e: any) {
    value = e.target.value;

    let parsed = parsePhoneNumberFromString(prefix + value);

    if (parsed) {
      dispatch("update", {
        phone: parsed.number,
      });

      if (!parsed.isValid() && parsed.number.startsWith("+44")) {
        parsed = parsePhoneNumberFromString(parsed.number.replace("+4444", "+44"));

        if (parsed?.isValid()) {
          value = String(parsed.nationalNumber);
        }
      } else {
        value = String(parsed.nationalNumber);
      }
    } else {
      dispatch("update", { phone: "" });
    }
  }

  function phoneBlur() {
    const parsed = parsePhoneNumberFromString(prefix + value);
    if (parsed) {
      value = String(parsed.nationalNumber);

      if (parsed?.number.length > 0 && !parsed?.isValid()) {
        error = "invalid phone number";
      } else {
        error = null;
      }
    } else {
      error = null;
    }
  }

  function phoneSubmit(e: KeyboardEvent) {
    if (e.key === "Enter") {
      ref.blur();
      if (!error) {
        dispatch("submit");
      }
    }
  }
</script>

<div class="outer-container">
  <div class="container">
    <div class="prefix cursor-oversized" on:click={openSelect}>
      <select
        class="prefix-select cursor-oversized"
        name="phoneCode"
        id="phoneCode"
        on:change={prefixChanged}
        on:blur={prefixChanged}
        {disabled}
      >
        {#each phonePrefixes as prefix}
          <option value={prefix.code}>
            {`${prefix.code} ${prefix.callingCode}`}
          </option>
        {/each}
      </select>
      <Text ocd color="transparency-dark" size="medium-large">{countryCode}</Text>

      <Stack horizontal>
        <Text size="larger">{prefix}</Text>
        <img
          class="dropdown"
          src="static/assets/icons/dropdown.svg"
          width={33}
          height={33}
          alt="dropdown"
        />
      </Stack>
    </div>
    <div class="phone">
      <Text ocd color="transparency-dark" size="medium-large">phone number</Text>
      <input
        class="input text-larger"
        bind:this={ref}
        on:input={phoneChanged}
        on:blur={phoneBlur}
        on:keypress={phoneSubmit}
        bind:value
        type="text"
        placeholder="0000000000"
        {disabled}
      />
    </div>
  </div>
  {#if error}
    <div class="error">
      <div class="icon-container">
        <img src="static/assets/icons/icon-info.svg" alt="info" width={11} height={11} />
      </div>
      <div class="error-text">
        <Text ocd>{error}</Text>
      </div>
    </div>
  {/if}
</div>

<style>
  .outer-container {
    display: flex;
    flex-direction: column;
  }

  .container {
    position: relative;
    display: flex;
    flex: 1;
  }

  .prefix-select {
    display: block;
    border: none;
    opacity: 0;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    opacity: 0;
    position: absolute;
    color: var(--rekki-transparency-dark);
  }

  .prefix {
    display: flex;
    flex-direction: column;
    padding: 23px 18px 18px;
    border-radius: 20px;
    background-color: #fff;
    gap: 12px;
  }

  .dropdown {
    padding-left: 16px;
    padding-bottom: 8px;
    margin-top: -8px;
  }

  .phone {
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: 23px 18px 18px;
    margin-left: 10px;
    background-color: #fff;
    z-index: 99;
    border-radius: 20px;
    gap: 8px;
  }

  .input {
    border-width: 0px;
    border: none;
    outline-width: 0;
    background-color: #fff;
  }

  .error {
    background-color: var(--rekki-warning);
    margin-top: 8px;
    border-radius: 8px;
    padding: 9px 10px;
    display: flex;
    flex-direction: row;
  }

  .error-text {
    margin-top: 1px;
  }

  .icon-container {
    width: 20px;
    height: 20px;
    border-radius: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: var(--rekki-light);
    margin-right: 8px;
  }

  .input::placeholder {
    color: var(--rekki-transparency-dark);
  }
</style>
